"use client";
import React, { useEffect, useState, useRef, useCallback } from "react";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import { OffersOffer } from "@/services/dataTypes";
import { apiRandomOfferBanners } from "@/apis/offers";
import { filterOfferBanners, getBannerDimensions } from "@/constants/hooks";
import Banner from "./Banner";
import { faChevronLeft, faChevronRight, FontAwesomeIcon } from "@/constants/icons";

interface Props {
  companyId: string;
  mer_slug: string;
  slug_type: string;
  domain: string;
}

const HorizontalBannerSlider = ({ companyId, slug_type, mer_slug, domain }: Props) => {
  const [offers, setOffers]           = useState<OffersOffer[]>([]);
  const [loading, setLoading]         = useState(false);
  const [hasMore, setHasMore]         = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [pagination, setPagination]   = useState<{
    current_page: number; last_page: number; next_page: number | null;
  } | null>(null);

  const latestRequest      = useRef(0);
  const MAX_EMPTY_ATTEMPTS = 1;
  const emptyAttempts      = useRef(0);
  const sliderSectionRef   = useRef<HTMLElement | null>(null);
  const [sliderInView, setSliderInView]     = useState(false);
  const [isSliderReady, setIsSliderReady]   = useState(false);
  const autoplayRef = useRef<NodeJS.Timeout | null>(null);

  const offersRef       = useRef(offers);
  const hasMoreRef      = useRef(hasMore);
  const loadingRef      = useRef(loading);
  const paginationRef   = useRef(pagination);
  const sliderInViewRef = useRef(sliderInView);

  useEffect(() => { offersRef.current      = offers;    }, [offers]);
  useEffect(() => { hasMoreRef.current     = hasMore;   }, [hasMore]);
  useEffect(() => { loadingRef.current     = loading;   }, [loading]);
  useEffect(() => { paginationRef.current  = pagination;}, [pagination]);
  useEffect(() => { sliderInViewRef.current = sliderInView; }, [sliderInView]);

  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    loop: true,
    slides: { perView: 1 },
    mode: "snap",
    created: () => setIsSliderReady(true),
    slideChanged: (s) => {
      setCurrentSlide(s.track.details.rel);
      if (
        sliderInViewRef.current &&
        s.track.details.rel === offersRef.current.length - 1 &&
        hasMoreRef.current &&
        !loadingRef.current &&
        paginationRef.current?.next_page
      ) {
        getOffers(paginationRef.current.next_page);
      }
    },
  });

  const startAutoplay = () => {
    if (!slider.current || !isSliderReady || autoplayRef.current) return;
    autoplayRef.current = setInterval(() => slider.current?.next(), 6000);
  };
  const stopAutoplay = () => {
    if (autoplayRef.current) { clearInterval(autoplayRef.current); autoplayRef.current = null; }
  };

  useEffect(() => {
    if (!slider.current || !isSliderReady) return;
    startAutoplay();
    return () => stopAutoplay();
  }, [isSliderReady]);

  const getOffers = useCallback(async (pageNum: number, reset = false) => {
    setLoading(true);
    const requestId = Date.now();
    latestRequest.current = requestId;
    try {
      const bannerOffers      = await apiRandomOfferBanners(companyId, pageNum);
      if (latestRequest.current !== requestId) return;
      const apiOffers         = bannerOffers?.data?.offers || [];
      const filterOfferBanner = filterOfferBanners(apiOffers, 300, 1000, 0, 150);
      const paginationData    = bannerOffers?.data?.pagination || {};

      setPagination({ current_page: paginationData.current_page, last_page: paginationData.last_page, next_page: paginationData.next_page });

      if (filterOfferBanner.length === 0 && paginationData.next_page && emptyAttempts.current < MAX_EMPTY_ATTEMPTS) {
        emptyAttempts.current += 1;
        await getOffers(paginationData.next_page, false);
        return;
      } else { emptyAttempts.current = 0; }

      if (reset)                         setOffers(filterOfferBanner);
      else if (filterOfferBanner.length) setOffers((prev) => [...prev, ...filterOfferBanner]);

      setHasMore(paginationData.next_page !== null);
    } finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => {
    if (!sliderSectionRef.current) return;
    const observer = new window.IntersectionObserver(
      (entries) => setSliderInView(entries[0].isIntersecting), { threshold: 0.1 }
    );
    observer.observe(sliderSectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setOffers([]); setPagination(null); emptyAttempts.current = 0; getOffers(1, true);
  }, [companyId, getOffers]);

  useEffect(() => { if (slider.current) slider.current.update(); }, [offers]);

  /* ── Skeleton ── */
  if (!offers.length && loading) {
    return (
      <section className="max-w-[1440px] mx-auto px-4 md:px-10 my-6">
        <div className="w-full h-[200px] rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center gap-3 animate-pulse">
          <div className="w-4 h-4 border-[3px] border-[#D61C4E] border-t-transparent rounded-full animate-spin" />
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Loading…</span>
        </div>
      </section>
    );
  }

  if (!offers.length) return null;

  const total = offers.length;

  return (
    <section
      ref={sliderSectionRef}
      className="max-w-[1440px] mx-auto px-4 md:px-10 my-6"
      onMouseEnter={stopAutoplay}
      onMouseLeave={startAutoplay}
    >
      {/* ── Shell ── */}
      <div className="group relative rounded-2xl overflow-hidden bg-[#0f172a]">
        {/* Top accent */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-white/30 to-transparent z-10" />

        {/* Decorative blobs inside the dark bg */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-[#D61C4E]/20 rounded-full blur-[80px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#D61C4E]/10 rounded-full blur-[80px] pointer-events-none" />

        {/* Side label — left */}
        <div className="absolute left-0 top-0 bottom-0 w-14 z-10 flex flex-col items-center justify-center gap-2 border-r border-white/5 pointer-events-none">
          <div className="flex flex-col items-center gap-1.5">
            {/* Vertical "DEALS" text */}
            <span
              className="text-[8px] font-black uppercase tracking-[0.4em] text-white/25"
              style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
            >
              Exclusive
            </span>
            <div className="w-px h-8 bg-white/10" />
            <span className="text-[8px] font-black uppercase tracking-[0.4em] text-[#D61C4E]/60"
              style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
            >
              Deals
            </span>
          </div>
        </div>

        {/* Side label — right */}
        <div className="absolute right-0 top-0 bottom-0 w-14 z-10 flex flex-col items-center justify-center gap-2 border-l border-white/5 pointer-events-none">
          {/* Slide counter */}
          <div className="flex flex-col items-center gap-1">
            <span className="text-[11px] font-black text-white tabular-nums">
              {String(currentSlide + 1).padStart(2, "0")}
            </span>
            <div className="w-px h-5 bg-white/20" />
            <span className="text-[11px] font-bold text-white/30 tabular-nums">
              {String(total).padStart(2, "0")}
            </span>
          </div>
        </div>

        {/* Keen slider — inset from the side labels */}
        <div className="mx-14">
          <div ref={sliderRef} className="keen-slider">
            {offers.map((item: OffersOffer, i: number) => {
              const dimension = getBannerDimensions(item);
              return (
                <div
                  key={i}
                  className="keen-slider__slide flex justify-center items-center py-8 px-4 min-h-[200px] md:min-h-[240px]"
                >
                  <Banner
                    data={item}
                    height={dimension?.height}
                    width={dimension?.width}
                    offerLink={null}
                    mer_slug={mer_slug}
                    slug_type={slug_type}
                    domain={domain}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Prev arrow ── */}
        <button
          onClick={() => slider.current?.prev()}
          aria-label="Previous"
          className="
            absolute left-14 top-1/2 -translate-y-1/2 z-20 ml-3
            w-8 h-8 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm
            flex items-center justify-center
            text-white/60 hover:bg-[#D61C4E] hover:text-white hover:border-[#D61C4E]
            opacity-0 group-hover:opacity-100
            transition-all duration-300
          "
        >
          <FontAwesomeIcon icon={faChevronLeft} className="w-2.5 h-2.5" />
        </button>

        {/* ── Next arrow ── */}
        <button
          onClick={() => slider.current?.next()}
          aria-label="Next"
          className="
            absolute right-14 top-1/2 -translate-y-1/2 z-20 mr-3
            w-8 h-8 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm
            flex items-center justify-center
            text-white/60 hover:bg-[#D61C4E] hover:text-white hover:border-[#D61C4E]
            opacity-0 group-hover:opacity-100
            transition-all duration-300
          "
        >
          <FontAwesomeIcon icon={faChevronRight} className="w-2.5 h-2.5" />
        </button>

        {/* ── Bottom bar: dot pagination ── */}
        <div className="flex items-center justify-center gap-1.5 pb-4">
          {offers.map((_, idx) => (
            <button
              key={idx}
              onClick={() => slider.current?.moveToIdx(idx)}
              aria-label={`Slide ${idx + 1}`}
              className={`rounded-full transition-all duration-300 ${
                currentSlide === idx
                  ? "w-6 h-1.5 bg-[#D61C4E]"
                  : "w-1.5 h-1.5 bg-white/20 hover:bg-[#D61C4E]/50"
              }`}
            />
          ))}
          {loading && (
            <div className="w-3 h-3 border-2 border-[#D61C4E] border-t-transparent rounded-full animate-spin ml-1" />
          )}
        </div>
      </div>
    </section>
  );
};

export default HorizontalBannerSlider;
